/**
 * Comment Form Component
 * ======================
 * Form for creating and editing comments.
 */

import { useState } from 'react'
import { Button, Spinner } from '../common'

interface CommentFormProps {
  onSubmit: (content: string) => Promise<void>
  onCancel?: () => void
  initialContent?: string
  placeholder?: string
  submitLabel?: string
  isReply?: boolean
  autoFocus?: boolean
}

export function CommentForm({
  onSubmit,
  onCancel,
  initialContent = '',
  placeholder = 'Write a comment...',
  submitLabel = 'Post',
  isReply = false,
  autoFocus = false,
}: CommentFormProps) {
  const [content, setContent] = useState(initialContent)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedContent = content.trim()
    if (!trimmedContent) {
      setError('Comment cannot be empty')
      return
    }

    if (trimmedContent.length > 2000) {
      setError('Comment must be 2000 characters or less')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await onSubmit(trimmedContent)
      setContent('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post comment')
    } finally {
      setIsSubmitting(false)
    }
  }

  const remainingChars = 2000 - content.length
  const showCharCount = content.length > 1800

  return (
    <form onSubmit={handleSubmit} className={isReply ? 'ml-12' : ''}>
      <div className="relative">
        <textarea
          value={content}
          onChange={(e) => {
            setContent(e.target.value)
            setError(null)
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          rows={isReply ? 2 : 3}
          className={`
            w-full px-4 py-3 border rounded-lg resize-none
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            ${error ? 'border-red-300' : 'border-gray-300'}
          `}
          disabled={isSubmitting}
        />
        {showCharCount && (
          <span
            className={`absolute bottom-3 right-3 text-xs ${
              remainingChars < 0 ? 'text-red-500' : 'text-gray-400'
            }`}
          >
            {remainingChars}
          </span>
        )}
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      <div className="mt-2 flex items-center gap-2 justify-end">
        {onCancel && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          size="sm"
          disabled={isSubmitting || !content.trim()}
        >
          {isSubmitting ? <Spinner size="sm" /> : submitLabel}
        </Button>
      </div>
    </form>
  )
}
